/**
 * Client-side prediction for the player's own movement.
 *
 * M0-05 asks for own movement to feel immediate — under 50 ms perceived — at
 * 150 ms simulated round-trip. There is only one way to do that: move the `@`
 * the instant the key is pressed, and be prepared to be told you were wrong.
 *
 * The rule that keeps it honest is that prediction uses **the server's own
 * arithmetic**, imported from `@annwn/protocol` rather than reimplemented
 * here. A predictor with its own idea of what a diagonal costs would agree
 * with the server most of the time and disagree exactly when it mattered — in
 * a fight, at a doorway — which is worse than not predicting at all.
 *
 * The client still predicts only what it can know: terrain it has been sent,
 * and its own cooldown. It does not predict other actors, because it cannot
 * see the intents they submitted this tick.
 */

import {
  canMoveAt,
  DIRECTION_VECTORS,
  MILLITICKS_PER_TICK,
  MOVE_CARRY_LIMIT,
  stepCost,
  TILE_DEFS,
  type Direction,
  type SelfSnapshot,
  type TileDef,
} from '@annwn/protocol';

import type { ClientWorld } from './client-world.js';

/** A move the client has drawn but the server has not yet confirmed. */
export interface PendingMove {
  readonly seq: number;
  readonly direction: Direction;
}

export interface PredictedSelf {
  x: number;
  y: number;
  facing: Direction;
  moveReadyAt: number;

  /**
   * How fast this body still walks (M2-03), from the last snapshot.
   *
   * Carried in the prediction rather than looked up, because the predictor's
   * whole job is to run the *server's* arithmetic locally: a hamstrung player
   * whose client still stepped at full speed would rubber-band every stride,
   * which is precisely the snap-back prediction exists to avoid.
   */
  speed: number;
}

export class Prediction {
  private pending: PendingMove[] = [];

  /** Server truth as of the last snapshot, before predictions are re-applied. */
  private confirmed: PredictedSelf | null = null;

  /** Where the client is actually drawing the player. */
  private predicted: PredictedSelf | null = null;

  get self(): PredictedSelf | null {
    return this.predicted;
  }

  get inFlight(): number {
    return this.pending.length;
  }

  /**
   * Try to move. Returns the move to send, or `null` if the client can already
   * tell the server would refuse — a wall, or a cooldown that has not elapsed.
   *
   * Refusing locally is not an optimisation, it is the difference between a
   * responsive game and a rubber band: sending a move that will certainly be
   * rejected means drawing a step and then snapping back from it.
   */
  request(world: ClientWorld, direction: Direction, tick: number, seq: number): PendingMove | null {
    const self = this.predicted;
    if (!self) return null;
    if (!canMoveAt(self, tick)) return null;

    const [dx, dy] = DIRECTION_VECTORS[direction];
    const x = self.x + dx;
    const y = self.y + dy;

    if (!this.canEnter(world, x, y)) return null;

    const move: PendingMove = { seq, direction };
    this.pending.push(move);
    applyMove(self, direction, tick, TILE_DEFS[world.tileAt(x, y)]);
    return move;
  }

  /**
   * Fold in server truth, then replay everything it has not seen yet.
   *
   * This is the whole of reconciliation. Predictions the server has already
   * processed are dropped; the rest are re-applied on top of the authoritative
   * position, so a correction moves the player once rather than fighting the
   * inputs still in flight.
   */
  reconcile(world: ClientWorld, self: SelfSnapshot, ack: number, tick: number): void {
    this.confirmed = {
      x: self.x,
      y: self.y,
      facing: self.facing,
      moveReadyAt: self.moveReadyAt,
      speed: self.combat?.speed ?? 1,
    };

    this.pending = this.pending.filter((move) => move.seq > ack);
    this.predicted = { ...this.confirmed };

    for (const move of this.pending) {
      const [dx, dy] = DIRECTION_VECTORS[move.direction];
      const target = { x: this.predicted.x + dx, y: this.predicted.y + dy };
      if (!this.canEnter(world, target.x, target.y)) continue;
      // The ground being stepped onto, exactly as the sim charges it (M3-01).
      // The client always has this tile: `canEnter` has just refused to predict
      // a step into anything unexplored.
      applyMove(this.predicted, move.direction, tick, TILE_DEFS[world.tileAt(target.x, target.y)]);
    }
  }

  /**
   * The server said no. Drop that prediction and rebuild from the last
   * confirmed position — the snapshot that follows will settle it either way,
   * but correcting now saves a frame of standing somewhere impossible.
   */
  reject(seq: number): void {
    this.pending = this.pending.filter((move) => move.seq !== seq);
    if (this.confirmed) this.predicted = { ...this.confirmed };
  }

  /** How far the drawn position has drifted from the server's, in tiles. */
  drift(): number {
    if (!this.confirmed || !this.predicted) return 0;
    return Math.max(
      Math.abs(this.predicted.x - this.confirmed.x),
      Math.abs(this.predicted.y - this.confirmed.y),
    );
  }

  private canEnter(world: ClientWorld, x: number, y: number): boolean {
    if (!world.inBounds(x, y)) return false;

    // Unexplored terrain is not assumed walkable: predicting a step into
    // something the client has never been shown is how a player ends up
    // walking into a wall that appears a moment later.
    if (!world.isExplored(x, y)) return false;
    if (!TILE_DEFS[world.tileAt(x, y)].passable) return false;

    for (const actor of world.entities) {
      if (actor.id !== world.self?.id && actor.x === x && actor.y === y) return false;
    }
    return true;
  }
}

/** The server's move arithmetic, applied to a predicted position. */
function applyMove(self: PredictedSelf, direction: Direction, tick: number, onto: TileDef): void {
  const [dx, dy] = DIRECTION_VECTORS[direction];
  self.x += dx;
  self.y += dy;
  self.facing = direction;

  // `stepCost` is the sim's own, imported rather than reimplemented: the one
  // place the client is allowed to do the server's arithmetic is the place it
  // has to get exactly right.
  const cost = stepCost(direction, self.speed, onto.moveCost);
  const now = tick * MILLITICKS_PER_TICK;
  self.moveReadyAt = Math.max(self.moveReadyAt, now - MOVE_CARRY_LIMIT) + cost;
}
